import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../firebase/config';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:443/api/v1';

export interface Patient {
  id: number;
  full_name: string;
  phone: string;
  last_visit_date: string | null;
}

export interface FullPatient {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  phone?: string;
  email?: string;
  address?: string;
  nit?: string;
  occupation?: string;
  age?: number;
  gender?: string;
  history_hypertension: boolean;
  history_diabetes: boolean;
  history_other?: string;
}

export interface RecentPatient {
  id: number;
  full_name: string;
  last_visit_date: string | null;
  last_visit_status: 'draft' | 'sale_pending' | 'payment_pending' | 'completed' | null;
}

export interface PaginatedRecentPatientsResponse {
  patients: RecentPatient[];
  total: number;
  limit: number;
  offset: number;
}

export interface ClinicalVisit {
  id: number;
  created_at: string;
  status: 'draft' | 'sale_pending' | 'payment_pending' | 'completed';
  diagnosis_snippet?: string | null;
  sales_summary?: {
    grand_total: number;
    amount_paid: number;
    currency: string;
  };
}

export interface ClinicalChartPatient {
  id: number;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  age?: number | null;
  address?: string;
  gender?: string;
  history_diabetes?: boolean;
  history_hypertension?: boolean;
  history_other?: string;
}

let authReadyPromise: Promise<User | null> | null = null;

const waitForAuthReady = async (): Promise<User | null> => {
  if (auth.currentUser) return auth.currentUser;

  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        (firebaseUser) => {
          unsubscribe();
          resolve(firebaseUser);
        },
        () => {
          unsubscribe();
          resolve(null);
        }
      );
    });
  }

  return authReadyPromise;
};

export const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser ?? await waitForAuthReady();
  if (!user) {
    throw new Error('No se ha iniciado sesión');
  }
  return await user.getIdToken();
};

export const searchPatients = async (query: string, limit: number = 10): Promise<Patient[]> => {
  if (!query || query.trim().length === 0) {
    throw new Error('Por favor ingrese un término de búsqueda');
  }

  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/patients/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorText = await response.text();
      throw new Error(errorText || 'Faltan parámetros requeridos para la búsqueda');
    }
    throw new Error('No se pudo realizar la búsqueda de pacientes. Intente nuevamente');
  }

  return await response.json();
};

export const getRecentPatients = async (
  limit: number = 5,
  offset: number = 0
): Promise<PaginatedRecentPatientsResponse> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/patients/recent?limit=${limit}&offset=${offset}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    throw new Error('No se pudo obtener los pacientes recientes. Intente nuevamente');
  }

  return await response.json();
};

export const getPatient = async (patientId: number | string): Promise<FullPatient> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/patients/${encodeURIComponent(patientId)}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 404) {
    throw new Error('Paciente no encontrado');
  }

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorText = await response.text();
      throw new Error(errorText || 'Solicitud inválida');
    }
    throw new Error('No se pudo obtener la información del paciente. Intente nuevamente');
  }

  const responseData = await response.json();
  return responseData.data;
};

export interface UpdatePatientRequest {
  first_name: string;
  last_name: string;
  phone?: string;
  email?: string;
  address?: string;
  nit?: string;
  occupation?: string;
  age?: number;
  gender?: string;
  history_hypertension: boolean;
  history_diabetes: boolean;
  history_other?: string;
}

export const updatePatient = async (
  patientId: number | string,
  data: UpdatePatientRequest
): Promise<{ success: boolean; error?: string }> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/patients/${encodeURIComponent(patientId)}`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 404) {
      throw new Error('Paciente no encontrado');
    }
    const errorData = await response.json();
    throw new Error(errorData.error || 'No se pudo actualizar el paciente');
  }

  return await response.json();
};

export interface CreatePatientRequest {
  first_name: string;
  last_name: string;
  phone: string;
  email?: string;
  address?: string;
  nit?: string;
  occupation?: string;
  age?: string;
  gender?: string;
  history_hypertension: boolean;
  history_diabetes: boolean;
  history_other?: string;
  symptoms?: string[];
}

export interface CreatePatientResponse {
  success: boolean;
  data: {
    id: string | number;
    full_name: string;
    clinic_id: string;
    created_at: string;
  };
}

export const createPatient = async (
  patientData: CreatePatientRequest
): Promise<CreatePatientResponse> => {
  const token = await getAuthToken();

  // Convert age to number if provided
  const payload = {
    ...patientData,
    age: patientData.age ? parseInt(patientData.age) : undefined,
  };

  const response = await fetch(`${API_BASE_URL}/patients`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('No se pudo verificar la autenticación. Código: IC-401');
    }
    if (response.status === 400) {
      const errorText = await response.text();
      throw new Error(errorText || 'Los datos del paciente son inválidos');
    }
    if (response.status === 500) {
      throw new Error('Error del servidor. Por favor intente más tarde');
    }
    throw new Error('No se pudo crear el paciente. Intente nuevamente');
  }

  return await response.json();
};

export const getClinicalVisitsByPatient = async (
  patientId: number | string
): Promise<ClinicalVisit[]> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/patients/${encodeURIComponent(patientId)}/clinical-visits`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      throw new Error('ID de paciente inválido');
    }
    throw new Error('No se pudo obtener el historial del paciente. Intente nuevamente');
  }

  const payload = await response.json();
  return Array.isArray(payload) ? payload : [];
};

// Clinical Visit Types
export interface Retinoscopy {
  od_static_val?: string;
  od_static_av?: string;
  od_dynamic_val?: string;
  od_dynamic_av?: string;
  oi_static_val?: string;
  oi_static_av?: string;
  oi_dynamic_val?: string;
  oi_dynamic_av?: string;
}

export interface VisualAcuity {
  od_distance_sc?: string;
  od_distance_cc?: string;
  od_ph?: string;
  od_dip_mm?: number;
  od_dnp_mm?: number;
  od_height_mm?: number;
  oi_distance_sc?: string;
  oi_distance_cc?: string;
  oi_ph?: string;
  oi_dip_mm?: number;
  oi_dnp_mm?: number;
  oi_height_mm?: number;
}

export interface Refraction {
  od_sphere_far?: number;
  od_cylinder_far?: number;
  od_axis_far?: number;
  od_add_far?: number;
  od_prisma_far?: string;
  od_sphere_near?: number;
  od_cylinder_near?: number;
  od_axis_near?: number;
  od_add_near?: number;
  od_prisma_near?: string;
  oi_sphere_far?: number;
  oi_cylinder_far?: number;
  oi_axis_far?: number;
  oi_add_far?: number;
  oi_prisma_far?: string;
  oi_sphere_near?: number;
  oi_cylinder_near?: number;
  oi_axis_near?: number;
  oi_add_near?: number;
  oi_prisma_near?: string;
}

export interface ClinicalVisitRequest {
  patient_id: number;
  diagnosis?: string;
  symptoms?: string;
  retinoscopy?: Retinoscopy;
  visual_acuity?: VisualAcuity;
  final_rx?: Refraction;
  lensometry?: Refraction;
}

export interface ClinicalVisitResponse {
  success: boolean;
  data: {
    id: number;
    patient_id: number;
    status: string;
    created_at: string;
  };
}

export const createClinicalVisit = async (
  visitData: ClinicalVisitRequest
): Promise<ClinicalVisitResponse> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/clinical-visits`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(visitData),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Los datos de la visita clínica son inválidos');
    }
    if (response.status === 500) {
      throw new Error('Error del servidor. Por favor intente más tarde');
    }
    throw new Error('No se pudo crear la visita clínica. Intente nuevamente');
  }

  return await response.json();
};

export interface UpdateClinicalVisitRequest {
  diagnosis?: string;
  symptoms?: string;
  retinoscopy?: Retinoscopy;
  visual_acuity?: VisualAcuity;
  final_rx?: Refraction;
  lensometry?: Refraction;
}

export const updateClinicalVisit = async (
  visitId: number | string,
  visitData: UpdateClinicalVisitRequest
): Promise<{ success: boolean; error?: string }> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/clinical-visits/${encodeURIComponent(visitId)}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(visitData),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 403) {
      throw new Error('No tiene permisos para editar esta visita');
    }
    if (response.status === 404) {
      throw new Error('Visita clínica no encontrada');
    }
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Los datos de la visita clínica son inválidos');
    }
    throw new Error('No se pudo actualizar la visita clínica. Intente nuevamente');
  }

  return await response.json();
};

export interface ClinicalChartData {
  visit: {
    id: number;
    patient_id: number;
    status: string;
    diagnosis: string;
    symptoms: string;
    created_at: string;
  } | null;
  patient: ClinicalChartPatient | null;
  retinoscopy: Retinoscopy | null;
  visual_acuity: VisualAcuity | null;
  final_rx: Refraction | null;
  lensometry: Refraction | null;
}

export interface ClinicalChartResponse {
  success: boolean;
  error: string;
  data: ClinicalChartData | null;
}

export const getClinicalChart = async (
  visitId: number | string
): Promise<ClinicalChartResponse> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/clinical-visits/${encodeURIComponent(visitId)}/chart`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 404) {
    throw new Error('Ficha clínica no encontrada');
  }

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      throw new Error('ID de visita inválido');
    }
    throw new Error('No se pudo obtener la ficha clínica. Intente nuevamente');
  }

  return await response.json();
};

// Commercial Handoff Types
export interface CommercialHandoffData {
  patient: {
    id: number;
    full_name: string;
  } | null;
  visit: {
    id: number;
    patient_id: number;
    status: string;
    symptoms: string;
    diagnosis: string;
    created_at: string;
  } | null;
  refraction: {
    id: number;
    visit_id: number;
    od_sphere_far: number | null;
    od_cylinder_far: number | null;
    od_axis_far: number | null;
    od_add_far: number | null;
    od_prisma_far: string;
    od_sphere_near: number | null;
    od_cylinder_near: number | null;
    od_axis_near: number | null;
    od_add_near: number | null;
    od_prisma_near: string;
    oi_sphere_far: number | null;
    oi_cylinder_far: number | null;
    oi_axis_far: number | null;
    oi_add_far: number | null;
    oi_prisma_far: string;
    oi_sphere_near: number | null;
    oi_cylinder_near: number | null;
    oi_axis_near: number | null;
    oi_add_near: number | null;
    oi_prisma_near: string;
  } | null;
  visual_acuity: {
    id: number;
    visit_id: number;
    od_distance_sc: string;
    od_distance_cc: string;
    od_ph: string;
    od_dip_mm: number | null;
    od_dnp_mm: number | null;
    oi_distance_sc: string;
    oi_distance_cc: string;
    oi_ph: string;
    oi_dip_mm: number | null;
    oi_dnp_mm: number | null;
  } | null;
  sales_order: {
    id: number;
    visit_id: number;
    grand_total: number;
    currency: string;
    subtotal_optical: number | null;
    subtotal_extras: number | null;
    amount_paid: number | null;
    created_at: string;
    is_printed: boolean;
  } | null;
  optical_details: {
    id: number;
    order_id: number;
    lens_description: string;
    lens_price: number | null;
    frame_description: string;
    frame_price: number | null;
  } | null;
  extras: Array<{
    id: number;
    order_id: number;
    description: string;
    price: number;
  }>;
}

export interface CommercialHandoffResponse {
  success: boolean;
  error: string;
  data: CommercialHandoffData | null;
}

export const getCommercialHandoffData = async (
  visitId: number | string
): Promise<CommercialHandoffResponse> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/clinical-visits/${encodeURIComponent(visitId)}/commercial`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      throw new Error('ID de visita inválido');
    }
    if (response.status === 404) {
      throw new Error('Visita no encontrada');
    }
    throw new Error('No se pudo obtener la información comercial. Intente nuevamente');
  }

  return await response.json();
};

// Sales Order Types
export interface CreateSalesOrderRequest {
  visit_id: number;
  lens_description: string;
  lens_price: number | null;
  frame_description: string;
  frame_price: number | null;
  extras: Array<{
    description: string;
    price: number;
  }>;
}

export interface UpdateSalesOrderRequest {
  lens_description: string;
  lens_price: number | null;
  frame_description: string;
  frame_price: number | null;
  extras: Array<{
    description: string;
    price: number;
  }>;
  subtotal_optical: number | null;
  subtotal_extras: number | null;
  grand_total: number;
}

export interface SalesOrderResponse {
  success: boolean;
  error: string;
  data: {
    order_id: number;
    grand_total: number;
    currency: string;
  } | null;
}

export const createSalesOrder = async (
  orderData: CreateSalesOrderRequest
): Promise<SalesOrderResponse> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/sales-orders`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Los datos de la orden de venta son inválidos');
    }
    throw new Error('No se pudo crear la orden de venta. Intente nuevamente');
  }

  return await response.json();
};

export const updateSalesOrder = async (
  orderId: number,
  orderData: UpdateSalesOrderRequest
): Promise<SalesOrderResponse> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/sales-orders/${orderId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Los datos de la orden de venta son inválidos');
    }
    throw new Error('No se pudo actualizar la orden de venta. Intente nuevamente');
  }

  return await response.json();
};

export const finalizeSalesOrder = async (
  orderId: number,
  status: 'completed' | 'payment_pending' = 'completed',
  amountPaid?: number
): Promise<{ success: boolean; error: string }> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/sales-orders/${orderId}/finalize`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status, amount_paid: amountPaid }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'No se puede finalizar la orden');
    }
    throw new Error('No se pudo finalizar la orden de venta. Intente nuevamente');
  }

  return await response.json();
};

export type PaymentMethod = 'cash' | 'card' | 'transfer';

export interface SalesPayment {
  id: string;
  order_id: number;
  amount: number;
  method: PaymentMethod;
  paid_at: string;
  recorded_by_user_id?: number;
  note?: string;
  created_at: string;
}

export const addPayment = async (
  orderId: number,
  payment: { amount: number; method: PaymentMethod; note?: string }
): Promise<{ success: boolean; data: SalesPayment }> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/sales-orders/${orderId}/payments`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payment),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'No se pudo registrar el pago. Intente nuevamente');
  }

  return await response.json();
};

export const listPayments = async (
  orderId: number
): Promise<{ success: boolean; data: SalesPayment[] }> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/sales-orders/${orderId}/payments`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    throw new Error('No se pudieron cargar los pagos. Intente nuevamente');
  }

  return await response.json();
};

export interface WorkOrderData {
  clinic: {
    id: number;
    name: string;
  };
  patient: {
    id: number;
    full_name: string;
    nit: string;
    phone: string;
    address: string;
  };
  visit: {
    id: number;
    created_at: string;
    status: string;
    diagnosis: string;
  };
  refraction: {
    far: {
      od: {
        sphere: number | null;
        cylinder: number | null;
        axis: number | null;
        add: number | null;
        prisma: string;
      };
      oi: {
        sphere: number | null;
        cylinder: number | null;
        axis: number | null;
        add: number | null;
        prisma: string;
      };
    };
    near: {
      od: {
        sphere: number | null;
        cylinder: number | null;
        axis: number | null;
        add: number | null;
        prisma: string;
      };
      oi: {
        sphere: number | null;
        cylinder: number | null;
        axis: number | null;
        add: number | null;
        prisma: string;
      };
    };
  } | null;
  visual_acuity: {
    od_dip_mm: number | null;
    oi_dip_mm: number | null;
  } | null;
  sales: {
    order_id: number;
    lens_description: string;
    lens_price: number | null;
    frame_description: string;
    frame_price: number | null;
    extras: Array<{
      description: string;
      price: number;
    }>;
    grand_total: number;
    currency: string;
  } | null;
}

export interface WorkOrderResponse {
  success: boolean;
  error: string;
  data: WorkOrderData | null;
}

export const getWorkOrderData = async (
  visitId: number | string
): Promise<WorkOrderResponse> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/clinical-visits/${encodeURIComponent(visitId)}/work-order`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 403) {
      throw new Error('No tiene permisos para acceder a esta información');
    }
    if (response.status === 404) {
      throw new Error('Visita no encontrada');
    }
    if (response.status === 422) {
      throw new Error('La visita debe estar completada antes de generar la orden de trabajo');
    }
    throw new Error('No se pudo obtener la orden de trabajo. Intente nuevamente');
  }

  return await response.json();
};

export const markOrderAsPrinted = async (orderId: number): Promise<{ success: boolean; error: string }> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/sales-orders/${orderId}/mark-printed`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    throw new Error('No se pudo marcar la orden como impresa. Intente nuevamente');
  }

  return await response.json();
};

// Virtual Try-On Types
export interface StartTryOnResponse {
  success: boolean;
  error: string;
  job_id: string | null;
}

export interface TryOnStatusResponse {
  success: boolean;
  error: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | null;
  result: string | null;
}

export const startTryOn = async (
  visitId: number | string,
  photoBase64: string,
  frameDescription: string,
  outfit: string,
  quality: string
): Promise<StartTryOnResponse> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/clinical-visits/${encodeURIComponent(visitId)}/try-on`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ photo_base64: photoBase64, frame_description: frameDescription, outfit, quality }),
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 404) {
      throw new Error('Visita no encontrada');
    }
    throw new Error('No se pudo iniciar la generación de imágenes. Intente nuevamente');
  }

  return await response.json();
};

export interface TryOnQuotaResponse {
  success: boolean;
  configured: boolean;
  limit: number;
  used: number;
}

export const getTryOnQuota = async (): Promise<TryOnQuotaResponse> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/clinic/try-on-quota`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    throw new Error('No se pudo consultar la cuota de generación de imágenes');
  }

  return await response.json();
};

export const getTryOnStatus = async (jobId: string): Promise<TryOnStatusResponse> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/try-on/${encodeURIComponent(jobId)}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 404) {
      throw new Error('No se encontró el proceso de generación');
    }
    throw new Error('No se pudo consultar el estado de la generación. Intente nuevamente');
  }

  return await response.json();
};

// Dashboard Types
export interface DashboardData {
  monthly_revenue: {
    current: number;
    previous: number;
    change_percentage: number;
    currency: string;
    /** The selected month is still in progress, so `previous` covers the same elapsed stretch. */
    is_partial_month: boolean;
    /** False when the previous period had no revenue: there is no percentage to show. */
    has_comparison: boolean;
    /** Last day included in `previous`, e.g. "2026-06-24". */
    previous_through: string;
  };
  pending_payments: {
    count: number;
    total_amount: number;
    currency: string;
  };
  patients: {
    total: number;
    new_this_month: number;
  };
  visits_today: number;
  completed_visits_month: number;
  user_first_name: string;
  recent_activity: Array<{
    type: 'visit_created' | 'sale_completed' | 'partial_payment' | 'patient_created';
    patient_name: string;
    patient_id: number;
    visit_id?: number;
    amount?: number;
    timestamp: string;
  }>;
  pending_payment_list: Array<{
    patient_id: number;
    patient_name: string;
    visit_id: number;
    visit_date: string;
    grand_total: number;
    amount_paid: number;
    pending_amount: number;
  }>;
  period: {
    month: string;
    year: number;
  };
  /** IANA zone `period` was computed in — use it, not the browser clock, to decide "current month". */
  timezone: string;
}

export const getDashboardData = async (month?: string): Promise<DashboardData> => {
  const token = await getAuthToken();
  const params = month ? `?month=${month}` : '';

  const response = await fetch(`${API_BASE_URL}/dashboard${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Formato de mes inválido');
    }
    throw new Error('No se pudo obtener los datos del dashboard. Intente nuevamente');
  }

  const result = await response.json();
  return result.data;
};

export const sendVisitPdf = async (
  visitId: number | string,
  pdfBlob: Blob,
  recipients: string[]
): Promise<{ success: boolean; message?: string; sent_to?: string; error?: string }> => {
  const token = await getAuthToken();

  const formData = new FormData();
  formData.append('visitId', String(visitId));
  formData.append('pdf', pdfBlob, 'visit-order.pdf');
  formData.append('recipients', recipients.join(','));

  const response = await fetch(`${API_BASE_URL}/communications/send-visit-pdf`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorText = await response.text();
      throw new Error(errorText || 'Solicitud inválida');
    }
    if (response.status === 500) {
      throw new Error('Error del servidor. Por favor intente más tarde');
    }
    throw new Error('No se pudo enviar el correo. Intente nuevamente');
  }

  return await response.json();
};

// Reports Types
export interface MonthRevenue {
  month: string; // "YYYY-MM"
  revenue: number;
}

export interface IncomeCategoryBreakdown {
  lens_total: number;
  frame_total: number;
  extras_total: number;
}

export interface RevenueReportData {
  year: number;
  currency: string;
  year_total: number;
  monthly_avg: number;
  best_month: MonthRevenue;
  yoy_change_pct: number;
  months: MonthRevenue[];
  category_breakdown: IncomeCategoryBreakdown;
}

export const getRevenueReport = async (year: number): Promise<RevenueReportData> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/reports/revenue?year=${year}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Año inválido');
    }
    throw new Error('No se pudo obtener el reporte de ingresos. Intente nuevamente');
  }

  const result = await response.json();
  return result.data;
};

// ── Bulk patient/visit/sale import ───────────────────────────────────────────

export interface ImportRefraction {
  od_sphere_far?: number;
  od_cylinder_far?: number;
  od_axis_far?: number;
  od_add_far?: number;
  oi_sphere_far?: number;
  oi_cylinder_far?: number;
  oi_axis_far?: number;
  oi_add_far?: number;
}

export interface ImportVisualAcuity {
  od_distance_sc?: string;
  od_distance_cc?: string;
  oi_distance_sc?: string;
  oi_distance_cc?: string;
}

export interface ImportVisit {
  symptoms?: string;
  diagnosis?: string;
  visual_acuity?: ImportVisualAcuity;
  lensometry?: ImportRefraction;
  final_rx?: ImportRefraction;
}

export interface ImportSaleExtra {
  description: string;
  price: number;
}

export interface ImportSale {
  frame_description?: string;
  frame_price?: number;
  lens_description?: string;
  lens_price?: number;
  extras?: ImportSaleExtra[];
  amount_paid?: number;
}

export interface ImportPatient {
  first_name: string;
  last_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  nit?: string;
  occupation?: string;
  age?: number;
  gender?: string;
  history_hypertension: boolean;
  history_diabetes: boolean;
  history_other?: string;
}

export interface ImportRow {
  row_number: number;
  patient: ImportPatient;
  visit?: ImportVisit;
  sale?: ImportSale;
  visit_date?: string;
}

export interface ImportRowResult {
  row_number: number;
  status: 'created' | 'matched' | 'skipped';
  patient_id?: number;
  visit_id?: number;
  sale_id?: number;
  error?: string;
}

export interface ImportSummary {
  patients_created: number;
  patients_matched: number;
  visits_created: number;
  sales_created: number;
  skipped: number;
}

export interface ImportResponse {
  success: boolean;
  summary: ImportSummary;
  results: ImportRowResult[];
  error?: string;
}

export const importPatients = async (rows: ImportRow[]): Promise<ImportResponse> => {
  if (rows.length === 0) {
    throw new Error('No hay filas para importar');
  }

  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/patients/import`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rows }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    if (response.status === 400) {
      const errorText = await response.text();
      throw new Error(errorText || 'Los datos de importación son inválidos');
    }
    if (response.status === 500) {
      throw new Error('Error del servidor. Por favor intente más tarde');
    }
    throw new Error('No se pudo importar. Intente nuevamente');
  }

  return await response.json();
};

// ─── Subscription status ─────────────────────────────────────────────────────

export interface SubscriptionStatus {
  status: 'active' | 'pending' | 'blocked' | 'inactive' | 'error' | 'none';
  plan_name?: string;
  amount?: number;
  currency?: string;
  billing_cycle_type?: string;
  current_period_start?: string;
  current_period_end?: string;
  card_brand?: string;
  card_last_four?: string;
}

export const getSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/billing/subscription`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Could not retrieve subscription status');
  }

  const body = await response.json();
  if (!body.success || !body.data) {
    throw new Error(body.error || 'Could not retrieve subscription status');
  }
  return body.data as SubscriptionStatus;
};

// ── FEL (Guatemala electronic tax invoice) ───────────────────────────────────

export interface FELConfig {
  emitter_nit: string;
  emitter_name: string;
  commercial_name: string;
  establishment_code: number;
  address: string;
  postal_code: string;
  municipality: string;
  department: string;
  country: string;
  iva_affiliation: string;
  is_active: boolean;
}

export interface FELConfigStatus {
  success: boolean;
  enabled: boolean;
  config?: FELConfig;
  error?: string;
}

export interface FELDocument {
  id: string;
  order_id: number;
  authorization_number?: string;
  receptor_nit: string;
  total: number;
  currency: string;
  status: 'issued' | 'error' | 'cancelled';
  error_detail?: string;
  certified_at?: string;
  created_at: string;
}

export interface FELResponse {
  success: boolean;
  data?: FELDocument;
  error?: string;
  details?: string[];
}

// getFELConfigStatus reports whether the clinic has FEL enabled (drives the UI gate).
export const getFELConfigStatus = async (): Promise<FELConfigStatus> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/clinic/fel-config`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    throw new Error('No se pudo verificar la configuración de FEL');
  }
  return await response.json();
};

// saveFELConfig upserts the clinic's FEL emitter config (admin).
export const saveFELConfig = async (
  config: FELConfig & { contap_token: string; contap_base_url?: string }
): Promise<{ success: boolean; error?: string }> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/clinic/fel-config`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'No se pudo guardar la configuración de FEL');
  }
  return await response.json();
};

// ─── Clinic billing info (subscription-FEL receptor identity) ────────────────

export interface ClinicBillingInfo {
  tax_nit?: string;
  legal_name?: string;
  billing_email?: string;
  address?: string;
}

// getClinicBillingInfo returns the clinic's saved billing identity, or null when
// none has been set yet.
export const getClinicBillingInfo = async (): Promise<ClinicBillingInfo | null> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/clinic/billing-info`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    throw new Error('No se pudo cargar la información de facturación');
  }
  const data = await response.json();
  return data.config ?? null;
};

// saveClinicBillingInfo upserts the clinic's billing identity. Every field is
// optional; an all-blank form means "invoice me as Consumidor Final".
export const saveClinicBillingInfo = async (
  info: ClinicBillingInfo
): Promise<{ success: boolean; error?: string }> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/clinic/billing-info`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(info),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'No se pudo guardar la información de facturación');
  }
  return await response.json();
};

export interface ClinicPreferences {
  /** IANA zone the clinic's revenue months, "today" and imported dates are measured in. */
  timezone: string;
}

// TIMEZONE_OPTIONS is the picker's shortlist. The server accepts any valid IANA name, so
// adding entries here needs no backend change.
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/Guatemala', label: 'Guatemala (GMT-6)' },
  { value: 'America/El_Salvador', label: 'El Salvador (GMT-6)' },
  { value: 'America/Tegucigalpa', label: 'Honduras (GMT-6)' },
  { value: 'America/Managua', label: 'Nicaragua (GMT-6)' },
  { value: 'America/Costa_Rica', label: 'Costa Rica (GMT-6)' },
  { value: 'America/Belize', label: 'Belice (GMT-6)' },
  { value: 'America/Panama', label: 'Panamá (GMT-5)' },
  { value: 'America/Bogota', label: 'Colombia (GMT-5)' },
  { value: 'America/Mexico_City', label: 'Ciudad de México (GMT-6)' },
  { value: 'America/Chicago', label: 'EE. UU. — Centro' },
  { value: 'America/New_York', label: 'EE. UU. — Este' },
  { value: 'UTC', label: 'UTC' },
];

// getClinicPreferences returns the clinic's reporting settings.
export const getClinicPreferences = async (): Promise<ClinicPreferences> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/clinic/preferences`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Su sesión ha expirado. Por favor inicie sesión nuevamente');
    }
    throw new Error('No se pudieron cargar las preferencias de la clínica');
  }
  const data = await response.json();
  return data.preferences;
};

// saveClinicPreferences persists the clinic's reporting settings. The server rejects a
// timezone it cannot resolve rather than storing one that would silently fall back.
export const saveClinicPreferences = async (
  prefs: ClinicPreferences
): Promise<{ success: boolean; error?: string }> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/clinic/preferences`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'No se pudo guardar la zona horaria');
  }
  return await response.json();
};

// getFEL returns the issued FEL for an order, or null if none has been issued.
export const getFEL = async (orderId: number): Promise<FELDocument | null> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/sales-orders/${orderId}/fel`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error('No se pudo obtener la factura');
  }
  const body: FELResponse = await response.json();
  return body.data ?? null;
};

// issueFEL emits a certified FEL for the order. Throws with the SAT rejection
// detail when emission is rejected.
export const issueFEL = async (
  orderId: number,
  receptorNit?: string
): Promise<FELDocument> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/sales-orders/${orderId}/fel`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(receptorNit ? { receptor_nit: receptorNit } : {}),
  });
  const body: FELResponse = await response.json().catch(() => ({ success: false }));
  if (!response.ok || !body.success || !body.data) {
    const detail = body.details?.length ? `: ${body.details.join('; ')}` : '';
    throw new Error((body.error || 'No se pudo emitir la factura') + detail);
  }
  return body.data;
};

// openFELPdf opens the certified invoice PDF in a new tab.
export const openFELPdf = async (orderId: number): Promise<void> => {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/sales-orders/${orderId}/fel/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error('No se pudo descargar el PDF de la factura');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
